import { useState, useEffect } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import AppLayout from "./components/AppLayout";
import LandingPage from "./pages/LandingPage";
import DashboardPage from "./pages/DashboardPage";
import VisionBoardPage from "./pages/VisionBoardPage";
import GoalsPage from "./pages/GoalsPage";
import BoardMeetingPage from "./pages/BoardMeetingPage";
import TodosPage from "./pages/TodosPage";
import GalleryPage from "./pages/GalleryPage";
import ProfilePage from "./pages/ProfilePage";
import AuthPage from "./pages/AuthPage";
import OnboardingPage from "./pages/OnboardingPage";
import JoinWorkspacePage from "./pages/JoinWorkspacePage";
import AdminPage from "./pages/AdminPage";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

function AppRoutes() {
  const { user, profile, loading, isAdmin } = useAuth();
  const [authCallbackPending, setAuthCallbackPending] = useState(false);

  // Detect auth callback hash on mount — block routing until session resolves
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    const params = new URLSearchParams(hash.substring(1));
    const error = params.get("error");
    const errorDescription = params.get("error_description");

    if (error) {
      console.error("Auth error from magic link:", error, errorDescription);
      toast.error("Sign-in link expired", {
        description: "Please request a new magic link and click it right away.",
        duration: 8000,
      });
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }

    // If hash contains access_token, Supabase client is processing it.
    // Block route rendering so we don't flash LandingPage on "/".
    if (params.get("access_token")) {
      setAuthCallbackPending(true);
    }
  }, []);

  // Once auth state settles (user resolved or loading done), clear pending flag
  useEffect(() => {
    if (authCallbackPending && !loading) {
      setAuthCallbackPending(false);
    }
  }, [loading, authCallbackPending]);

  // Global unhandled rejection handler
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled rejection:", event.reason);
      toast.error("Something went wrong. Please try again.");
      event.preventDefault();
    };
    window.addEventListener("unhandledrejection", handleRejection);
    return () => window.removeEventListener("unhandledrejection", handleRejection);
  }, []);

  if (loading || authCallbackPending) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground font-display text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/app" element={<AuthPage />} />
        <Route path="/app/*" element={<AuthPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  if (!profile?.onboarding_complete || !profile?.workspace_id) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/app/*" element={<OnboardingOrJoin />} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/app" replace />} />
      <Route path="/app" element={<AppLayout><DashboardPage /></AppLayout>} />
      <Route path="/app/vision" element={<AppLayout><VisionBoardPage /></AppLayout>} />
      <Route path="/app/goals" element={<AppLayout><GoalsPage /></AppLayout>} />
      <Route path="/app/reflect" element={<AppLayout><BoardMeetingPage /></AppLayout>} />
      <Route path="/app/todos" element={<AppLayout><TodosPage /></AppLayout>} />
      <Route path="/app/gallery" element={<AppLayout><GalleryPage /></AppLayout>} />
      <Route path="/app/profile" element={<AppLayout><ProfilePage /></AppLayout>} />
      <Route path="/app/admin" element={
        isAdmin
          ? <AppLayout><AdminPage /></AppLayout>
          : <Navigate to="/app" replace />
      } />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function OnboardingOrJoin() {
  const { profile } = useAuth();
  const [hasInvite, setHasInvite] = useState<boolean | null>(null);

  useEffect(() => {
    if (!profile?.email) return;
    supabase
      .from("workspace_invites")
      .select("id")
      .eq("invited_email", profile.email.toLowerCase())
      .eq("accepted", false)
      .limit(1)
      .then(({ data }) => {
        setHasInvite(data && data.length > 0);
      });
  }, [profile?.email]);

  if (hasInvite === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Setting up...</p>
        </div>
      </div>
    );
  }

  return hasInvite ? <JoinWorkspacePage /> : <OnboardingPage />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
